import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, Image, TouchableOpacity, useWindowDimensions, ImageStyle, TextStyle, ViewStyle, ActivityIndicator, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../navigation/types';
import { Typography, Button } from './ui';
import { layout, fonts, typography } from '../styles';
import Ionicons from '@expo/vector-icons/Ionicons';
import { api, getFileUrl } from '../services/api';
import { retryAsync } from '../utils/retry';
import { MasterWithLiveNow } from '../types/auth';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { analytics } from '../services/analytics';
import { useAuth } from '../context/AuthContext';
import { useCalendar } from '../context/CalendarContext';
import { PracticeJoinModal } from './PracticeJoinModal';
import { AGAPE_PRACTICE_URL } from '../config/links';
import { AGAPE_PRACTICE_REMINDER_ID } from '../config/agapePractice';
import { isPublicDiscoverableEvent } from '../utils/ownPublications';
import { ShareModal } from './ShareModal';
import { type ShareTarget } from '../services/shareAnalytics';
import { shareOrOpenModal } from '../utils/roomShare';
import { uiFont } from '../styles/agapellaFonts';

import { ACCENT_TEXT, accentTextOn, ON_ACCENT_TEXT } from '../styles/accentText';
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

// Initials placeholder palette — mirrors Masters.tsx so a master without a
// real photoUrl shows initials on a colour rather than a stranger's stock
// photo (avoids the avatar mismatch between the home block and /masters).
const INITIALS_PALETTE = ['#E98245', '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EC4899', '#06B6D4', '#84CC16'];

const masterInitials = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.slice(0, 2) || '·').toUpperCase();
};



interface MasterCardData {
  id: number;
  /** Owning app user id — for the level badge (CORE-269). */
  userId?: string;
  name: string;
  role: string;
  /** Resolved avatar URL (master.photoUrl through getFileUrl). Null → initials placeholder. */
  photoUrl: string | null;
  isLiveNow?: boolean;
  currentLiveId?: number;
  eventId?: number;
  eventDate?: string;
  eventType?: string;
  tags?: string[];
}

export const Hero: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 1100;
  const { isDark, colors: themeColors } = useTheme();
  const { t, i18n } = useI18n('home');
  const { isAuthenticated } = useAuth();

  const [showShareModal, setShowShareModal] = useState(false);
  // CORE-280 — unified share for the Agape-practice invite. Web opens the
  // branded ShareModal; native keeps the system share sheet.
  const [showBrandedShare, setShowBrandedShare] = useState(false);
  const agapeShareUrl = Platform.OS === 'web'
    ? `${window.location.origin}/agape-practice`
    : AGAPE_PRACTICE_URL;
  const agapeShareMessage = `${t('agapeBanner.shareMessage')}\n${agapeShareUrl}`;
  const agapeShareTitle = 'Agapē Practice';
  const agapeShareTarget: ShareTarget = { entityType: 'practice', entityKey: 'agape_practice', surface: 'hero' };

  const [showGuestModal, setShowGuestModal] = useState(false);
  const { reminderIds, addToCalendar } = useCalendar();
  const isJoined = reminderIds.has(AGAPE_PRACTICE_REMINDER_ID);

  const [masterGrid, setMasterGrid] = useState<MasterCardData[]>([]);
  const [isLoadingMasters, setIsLoadingMasters] = useState(true);

  // Weekly rotating master for the Agapē Practice banner
  const weeklyMaster = React.useMemo(() => {
    if (masterGrid.length === 0) return null;
    // Rotate based on ISO week number so it changes every Sunday
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const weekNum = Math.ceil(((now.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
    return masterGrid[weekNum % masterGrid.length];
  }, [masterGrid]);

  useEffect(() => {
    let cancelled = false;
    const loadMasters = async () => {
      try {
        // CORE-276 — the floating-masters grid must not vanish forever on a
        // single transient failure. In RU the edge 504s under burst load
        // (hard refresh), and the api-ru fallback doesn't retry 5xx, so the
        // call reaches us as a throw. Retry the (critical) master fetch with
        // backoff so the hero rides out the blip; the other two calls stay
        // best-effort with their own fallbacks.
        const [profiles, liveMasters, eventsRes] = await Promise.all([
          // CORE-322 — ranked home carousel (only masters with material).
          retryAsync(() => api.getPublicMasterProfiles({ sort: 'ranked' }), { retries: 4, baseDelayMs: 500 }),
          api.getMastersWithLiveNow().catch(() => [] as MasterWithLiveNow[]),
          api.getEvents().catch(() => ({ items: [], totalCount: 0 })),
        ]);
        if (cancelled) return;
        const liveMap = new Map(liveMasters.map(m => [m.id, m]));

        const now = new Date();
        const upcomingEvents = (eventsRes.items || [])
          // CORE-226 — /api/events returns the OWNER their own private events
          // (for the «События» management tab); the home hero is public
          // discovery, so private events must be excluded here too.
          .filter(e => e.source === 'master' && e.masterProfileId && isPublicDiscoverableEvent(e) && new Date(e.startDateUtc) > now)
          .sort((a, b) => new Date(a.startDateUtc).getTime() - new Date(b.startDateUtc).getTime());

        const eventMap = new Map<number, any>();
        upcomingEvents.forEach(e => {
          if (e.masterProfileId && !eventMap.has(e.masterProfileId)) {
            eventMap.set(e.masterProfileId, e);
          }
        });

        const cards: MasterCardData[] = profiles.map((m, idx) => {
          const liveInfo = liveMap.get(m.id);
          const eventInfo = eventMap.get(m.id);

          // Real practice directions set by the master (Settings → Направления),
          // surfaced as the tagline under the name. Capped so the single line
          // stays readable; numberOfLines={1} truncates any overflow anyway.
          const tags = (m.directions ?? []).slice(0, 3);

          return {
            id: m.id,
            userId: m.userId,
            // Master names are shown verbatim from the API — never translated.
            name: m.name,
            role: m.intention || '',
            photoUrl: getFileUrl(m.photoUrl),
            isLiveNow: !!liveInfo,
            currentLiveId: liveInfo?.currentLiveId,
            eventId: eventInfo?.id,
            eventDate: eventInfo?.startDateUtc,
            eventType: eventInfo?.eventType,
            tags,
          };
        });

        cards.sort((a, b) => {
          if (a.eventDate && !b.eventDate) return -1;
          if (!a.eventDate && b.eventDate) return 1;
          if (a.eventDate && b.eventDate) return new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime();
          return 0;
        });

        setMasterGrid(cards.slice(0, 10));
      } catch (err) {
        if (!cancelled) setMasterGrid([]);
      } finally {
        if (!cancelled) setIsLoadingMasters(false);
      }
    };
    loadMasters();
    return () => { cancelled = true; };
  }, []);



  const renderMasterCard = (m: MasterCardData, idx: number, compact = false) => (
    <TouchableOpacity
      key={`${m.id}-${idx}`}
      activeOpacity={0.88}
      onPress={() => {
        if (m.isLiveNow && m.currentLiveId) {
          navigation.navigate('LiveDetail', { id: m.currentLiveId, masterProfileId: m.id });
        } else if (m.eventId) {
          navigation.navigate('MasterDetail', { masterId: m.id, openEventModalId: m.eventId } as any);
        } else {
          navigation.navigate('MasterDetail', { masterId: m.id });
        }
      }}
      style={[styles.masterCardWrapper, compact && { width: 160, height: 210, aspectRatio: undefined }]}
      className={Platform.OS === 'web' ? 'master-elite-card' : ''}
    >
      {/* Full-bleed photo — real master avatar; initials placeholder when
          none, matching the /masters list (no unrelated stock photos). */}
      {m.photoUrl ? (
        <Image source={{ uri: m.photoUrl }} style={styles.masterImage as ImageStyle} />
      ) : (
        <View style={[styles.masterImage as ImageStyle, styles.masterImagePlaceholder, { backgroundColor: INITIALS_PALETTE[m.id % INITIALS_PALETTE.length] }] as any}>
          <Typography style={styles.masterInitials}>{masterInitials(m.name)}</Typography>
        </View>
      )}

      {/* Bottom gradient overlay */}
      {Platform.OS === 'web' && (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, top: '30%',
          background: 'linear-gradient(to top, rgba(0,10,18,0.92) 0%, rgba(0,10,18,0.5) 50%, transparent 100%)',
          borderRadius: '0 0 18px 18px',
        }} />
      )}

      {/* Live badge */}
      {m.isLiveNow && (
        <View style={styles.liveTagOverlay}>
          <View style={styles.liveDot} />
          <Typography style={styles.liveText}>LIVE</Typography>
        </View>
      )}

      {/* Info overlay — directly on photo */}
      <View style={styles.masterInfoOverlay}>
        <View style={styles.masterNameRow}>
          <Typography style={styles.masterName} numberOfLines={1}>{m.name}</Typography>
        </View>
        {(m.role || (m.tags && m.tags.length > 0)) && (
          <Typography style={styles.masterTagline} numberOfLines={1}>
            {m.role || m.tags!.join('  ·  ')}
          </Typography>
        )}
      </View>
    </TouchableOpacity>
  );

  // Team paused the open-practice banner (web + native). Flip to re-enable.
  const AGAPE_BANNER_ENABLED: boolean = false;

  return (
    <View style={[styles.hero, { backgroundColor: themeColors.pageBg }, !isDesktop && { paddingBottom: 24 }]}>
      <View style={styles.container}>
        <View style={[styles.layout, isDesktop ? styles.layoutDesktop : styles.layoutMobile, !isDesktop && { gap: 16 }]}>

          {/* Left Column */}
          <View style={[styles.leftCol, !isDesktop && { paddingLeft: 0, paddingTop: 0 }]} {...(Platform.OS === 'web' && { className: 'hero-left-col' } as any)}>

            {/* ── Headline ── */}
            <Typography variant={Platform.OS === 'web' ? 'displayH1' : 'h1'} style={[styles.headline, { color: themeColors.textPrimary }, !isDesktop && { fontSize: 34, lineHeight: 40, letterSpacing: -1.5, marginBottom: 20 }]}>
              {t('hero.headline')}{' '}
              <Typography variant={Platform.OS === 'web' ? 'displayH1' : 'h1'} style={[styles.headline, { color: ACCENT_TEXT }, !isDesktop && { fontSize: 34, lineHeight: 40, letterSpacing: -1.5, marginBottom: 20 }]}>{t('hero.headlineHighlight')}</Typography>
            </Typography>

            {/* ── Tagline ── */}
            <Typography style={[styles.descriptionText, { color: themeColors.textSecondary }, !isDesktop && { fontSize: 15, lineHeight: 22, marginBottom: 24 }]}>
              {t('hero.tagline')}
            </Typography>

            {/* ── Hero CTA — «Начать» + «Календарь» (restored per design approval).
                The soft entry point into the app: for guests these routes surface
                the AuthCTA / guest flow, for signed-in users they open the section
                directly. Reuses the existing `ctaRow` styling. */}
            <View style={[styles.ctaRow, !isDesktop && { marginBottom: 24, flexWrap: 'wrap' }]}>
              <Button
                variant="convex-fire"
                size="lg"
                onPress={() => { analytics.track('home_start_practice_clicked'); navigation.navigate('Practices' as any); }}
                rightIcon="arrow-forward"
                textStyle={{ textTransform: 'none' }}
              >
                {t('hero.startPractice')}
              </Button>
              <Button
                variant="convex-light"
                size="lg"
                onPress={() => { analytics.track('home_explore_schedule_clicked'); navigation.navigate('Lives' as any); }}
                textStyle={{ textTransform: 'none' }}
              >
                {t('hero.exploreSchedule')}
              </Button>
            </View>

            {/* Open-practice banner temporarily disabled (team decision) — paused
                on web + native while we decide what to do with it. To restore,
                flip the `false &&` guard below back on (or remove it). */}
            {AGAPE_BANNER_ENABLED && (<>
            {/* ═══ Agapē Practice — Modern Action Bar ═══ */}
            <TouchableOpacity
              activeOpacity={0.9}
              onPress={() => navigation.navigate('AgapePractice' as any)}
              style={[
                styles.agapeBanner,
                { backgroundColor: isDark ? themeColors.surfaceElevated : '#F4F7F6' },
                isDark && { borderColor: 'rgba(255,255,255,0.06)' },
                !isDesktop && { padding: 20 },
              ]}
            >
              {/* ── Background Aura (Tech/AI Premium Aesthetic) ── */}
              {Platform.OS === 'web' && (
                <>
                  {/* Glowing auras removed to prevent blurring out of the container bounds and staining the parent background */}
                </>
              )}

              {/* ── Content Layer (Horizontal layout for desktop) ── */}
              <View style={[styles.agapeBannerInner, isDesktop ? { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' } : {}]}>

                {/* Left Column: Text */}
                <View style={isDesktop ? { flex: 1, paddingRight: 40, zIndex: 1 } : { width: '100%', zIndex: 1 }}>
                  {/* Overline Tag */}
                  <View style={styles.agapeBannerOverlineRow}>
                    <View style={Platform.OS === 'web' ? {
                      width: 8, height: 8, borderRadius: 4, backgroundColor: '#E98245',
                      boxShadow: '0 0 12px rgba(233, 130, 69,0.6)'
                    } : styles.agapeBannerLiveDot} />
                    <Typography style={styles.agapeBannerOverline}>
                      {t('agapeBanner.overline')}
                    </Typography>
                  </View>

                  {/* Main Headline */}
                  <Typography style={[styles.agapeBannerHeadline, { color: themeColors.textPrimary }, !isDesktop && { fontSize: 28, lineHeight: 34 }]}>
                    {t('agapeBanner.titleBefore')}
                    <Typography style={[styles.agapeBannerHeadline, { color: accentTextOn('#FF7020') }, !isDesktop && { fontSize: 28, lineHeight: 34 }]}>
                      {t('agapeBanner.titleHighlight')}
                    </Typography>
                    {t('agapeBanner.titleAfter')}
                  </Typography>

                  {/* Subheadline */}
                  <Typography style={[styles.agapeBannerSubheadline, { color: themeColors.textSecondary, marginBottom: isDesktop ? 0 : 24 }, !isDesktop && { fontSize: 14, lineHeight: 22 }]}>
                    {t('agapeBanner.subheadline')}
                  </Typography>
                </View>

                {/* Right Column: CTA */}
                <View style={[styles.agapeBannerCtaRow, isDesktop ? { flexShrink: 0, flexDirection: 'column', alignItems: 'stretch', gap: 12, zIndex: 1 } : { width: '100%', justifyContent: 'flex-start', zIndex: 1 }]}>
                  <TouchableOpacity
                    style={styles.agapeJoinBtn}
                    activeOpacity={0.85}
                    onPress={(e: any) => {
                      e.stopPropagation();
                      if (!isAuthenticated) {
                        setShowGuestModal(true);
                        return;
                      }
                      if (isJoined) return; // Already joined — "Я иду" is a status, not a toggle
                      analytics.track('home_agape_join_clicked');
                      addToCalendar(AGAPE_PRACTICE_REMINDER_ID);
                      setShowShareModal(true);
                    }}
                  >
                    {isJoined ? (
                      <>
                        <Typography style={styles.agapeJoinText}>
                          {t('agapeBanner.ctaJoined')}
                        </Typography>
                        <Ionicons name="checkmark" size={16} color="#FFFFFF" style={{ marginLeft: 6 }} />
                      </>
                    ) : (
                      <>
                        <Typography style={styles.agapeJoinText}>
                          {t('agapeBanner.ctaJoin')}
                        </Typography>
                      </>
                    )}
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.agapeShareBtn}
                    activeOpacity={0.8}
                    onPress={(e: any) => {
                      e.stopPropagation();
                      // If the user already RSVP'd, show the celebratory
                      // modal (which has its own "Пригласить друзей" CTA).
                      // Otherwise — the user just wants to share.
                      if (isJoined) {
                        setShowShareModal(true);
                        return;
                      }
                      // CORE-786 — a phone gets the OS sheet, a desktop the popup.
                      void shareOrOpenModal(
                        { url: agapeShareUrl, title: agapeShareTitle, message: agapeShareMessage },
                        agapeShareTarget,
                        () => setShowBrandedShare(true),
                      );
                    }}
                    {...(Platform.OS === 'web' ? { className: 'invite-btn-glow' } as any : {})}
                  >
                    <Typography style={[styles.agapeShareText, { color: themeColors.textPrimary }]}>
                      {t('agapeBanner.inviteFriends')}
                    </Typography>
                  </TouchableOpacity>
                </View>

              </View>
            </TouchableOpacity>

            <PracticeJoinModal
              visible={showGuestModal}
              mode="guest"
              onClose={() => setShowGuestModal(false)}
              onLogIn={() => {
                setShowGuestModal(false);
                navigation.navigate('Login' as any);
              }}
              onSignUp={() => {
                setShowGuestModal(false);
                navigation.navigate('Register' as any);
              }}
            />

            <PracticeJoinModal
              visible={showShareModal}
              mode="joined"
              onClose={() => setShowShareModal(false)}
              onInvite={() => {
                // Close the celebration modal, then share.
                // CORE-786 — a phone gets the OS sheet, a desktop the popup.
                setShowShareModal(false);
                void shareOrOpenModal(
                  { url: agapeShareUrl, title: agapeShareTitle, message: agapeShareMessage },
                  agapeShareTarget,
                  () => setShowBrandedShare(true),
                );
              }}
            />

            <ShareModal
              visible={showBrandedShare}
              onClose={() => setShowBrandedShare(false)}
              url={agapeShareUrl}
              title={agapeShareTitle}
              message={agapeShareMessage}
              language={i18n.language}
              analytics={agapeShareTarget}
            />
            </>)}

            {/* Mobile master ribbon removed — masters now live on /Masters page */}
          </View>

          {/* Right Column: Master Ticker (Desktop only) */}
          {Platform.OS === 'web' && isDesktop && (
            <View style={styles.rightCol} {...({ className: 'hero-right-col' } as any)}>
              <View style={styles.visualStack} {...({ className: 'hero-visual-stack' } as any)}>
                {isLoadingMasters ? (
                  <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                    <ActivityIndicator size="small" color={ACCENT_TEXT} />
                  </View>
                ) : masterGrid.length === 0 ? (
                  <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', opacity: 0.3 }}>
                    <Ionicons name="people-outline" size={48} color={themeColors.textPrimary} />
                  </View>
                ) : (
                  <View style={styles.tickerLayout} {...({ className: 'hero-ticker-layout' } as any)}>
                    {(() => {
                      let safeGrid = [...masterGrid];
                      if (safeGrid.length > 0) {
                        while (safeGrid.length < 12) {
                          safeGrid = [...safeGrid, ...masterGrid];
                        }
                      }
                      return (
                        <>
                          <div className="ticker-col">
                            <div className="ticker-track-col">
                              {safeGrid.map((m, i) => renderMasterCard(m, i))}
                            </div>
                          </div>
                          <div className="ticker-col">
                            <div className="ticker-track-col ticker-track-col--reverse">
                              {[...safeGrid].reverse().map((m, i) => renderMasterCard(m, i))}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </View>
                )}
              </View>
            </View>
          )}

        </View>
      </View>
    </View >
  );
};

const styles = StyleSheet.create({
  hero: {
    paddingTop: Platform.OS === 'web' ? 110 : 70,
    backgroundColor: '#FFFFFF',
    paddingBottom: 60,
    minHeight: Platform.OS === 'web' ? 'auto' : undefined,
  } as ViewStyle,
  container: {
    maxWidth: layout.maxWidth,
    marginHorizontal: 'auto',
    paddingHorizontal: layout.paddingH,
    width: '100%',
  } as ViewStyle,
  layout: {
    gap: Platform.OS === 'web' ? 60 : 24,
  } as ViewStyle,
  layoutDesktop: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
  } as ViewStyle,
  layoutMobile: {
    flexDirection: 'column',
  } as ViewStyle,
  leftCol: {
    flex: 1,
    maxWidth: 800,
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    paddingLeft: Platform.OS === 'web' ? 24 : 0,
    paddingTop: Platform.OS === 'web' ? 40 : 0,
  } as ViewStyle,
  rightCol: {
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
    marginLeft: Platform.OS === 'web' ? 40 : 0,
  } as ViewStyle,
  headline: {
    marginBottom: 48,
    ...typography.h1Hero,
  } as TextStyle,
  descriptionText: {
    ...typography.body,
    fontSize: 20,
    maxWidth: '100%',
    marginBottom: 48,
    lineHeight: 32,
    opacity: 0.7,
  } as TextStyle,
  agapeBanner: {
    marginTop: 0,
    width: '100%',
    position: 'relative',
    borderRadius: 24,
    overflow: 'hidden',
    backgroundColor: '#FAFAFA', // ultra crisp AI aesthetic background
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.04)',
    padding: Platform.OS === 'web' ? 40 : 28,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 24px 60px rgba(0,0,0,0.03)',
      transition: 'transform 0.4s ease, box-shadow 0.4s ease',
    }),
  } as any,
  agapeBannerInner: {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  } as ViewStyle,
  agapeBannerOverlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  } as ViewStyle,
  agapeBannerLiveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#E98245', // brand orange
  } as ViewStyle,
  agapeBannerOverline: {
    fontSize: 12,
    ...uiFont(700),
    color: ACCENT_TEXT,
    letterSpacing: 2,
    textTransform: 'uppercase',
  } as TextStyle,
  agapeBannerHeadline: {
    ...typography.h2,
    fontSize: 44,
    marginBottom: 10,
    textAlign: 'left',
  } as TextStyle,
  agapeBannerSubheadline: {
    ...typography.body,
    fontSize: 18,
    lineHeight: 28,
    textAlign: 'left',
    maxWidth: 500,
    opacity: 0.8,
  } as TextStyle,
  agapeBannerCtaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 16,
    flexWrap: 'wrap',
  } as ViewStyle,
  agapeJoinBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2B2730',
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderRadius: 100,
    minWidth: 0,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      transform: 'translateZ(0)',
    }),
  } as any,
  agapeJoinText: {
    fontSize: 14,
    ...uiFont(700),
    color: '#FFFFFF',
    letterSpacing: 1,
    textTransform: 'uppercase',
  } as TextStyle,
  agapeShareBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderRadius: 100,
    borderWidth: 2,
    borderColor: '#FF7020',
    backgroundColor: 'transparent',
    minWidth: 0,
    shadowColor: '#FF7020',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 3,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 0 8px rgba(255,112,32,0.15)',
    } as any),
  } as any,
  agapeShareText: {
    fontSize: 14,
    ...uiFont(600),
    letterSpacing: 0.3,
  } as TextStyle,
  agapeBannerBottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.12)',
    gap: 0,
    flexWrap: 'wrap',
  } as ViewStyle,
  agapeBannerStatItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  } as ViewStyle,
  agapeBannerStatDivider: {
    width: 1,
    height: 14,
    backgroundColor: 'rgba(255,255,255,0.2)',
    marginHorizontal: 14,
  } as ViewStyle,
  agapeBannerStat: {
    fontSize: 13,
    ...uiFont(500),
    color: 'rgba(255,255,255,0.65)',
    letterSpacing: 0.2,
  } as TextStyle,
  visualStack: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
  } as ViewStyle,
  tickerLayout: {
    flexDirection: 'row',
    gap: 6,
    flex: 1,
    overflow: 'hidden',
  } as ViewStyle,
  masterCardWrapper: {
    width: '100%',
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
    aspectRatio: 0.78,
    flexShrink: 0,
    backgroundColor: '#0D1420',
    ...(Platform.OS === 'web' && { boxShadow: 'none' }),
  } as any,
  masterImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  } as ImageStyle,
  masterImagePlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  masterInitials: {
    color: '#FFFFFF',
    fontSize: 48,
    fontWeight: '700',
    letterSpacing: -1,
  } as TextStyle,
  masterInfoOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: Platform.OS === 'web' ? 14 : 10,
    paddingBottom: Platform.OS === 'web' ? 14 : 10,
    paddingTop: Platform.OS === 'web' ? 40 : 24,
    ...(Platform.OS !== 'web' && { backgroundColor: 'rgba(0,10,18,0.75)' }),
  } as ViewStyle,

  liveTagOverlay: {
    position: 'absolute',
    top: 16,
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderRadius: 100,
    ...(Platform.OS === 'web' && { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }),
  } as ViewStyle,
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#E98245',
  } as ViewStyle,
  liveText: {
    fontSize: 14,
    fontWeight: '700',
    color: ACCENT_TEXT,
    letterSpacing: 1,
    textTransform: 'uppercase',
  } as TextStyle,
  masterInfoContent: {
    paddingTop: 14,
    paddingBottom: 16,
    paddingHorizontal: 14,
    alignItems: 'center',
  } as ViewStyle,
  masterNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  masterName: {
    fontSize: Platform.OS === 'web' ? 22 : 14,
    color: '#FFFFFF',
    marginBottom: 3,
    letterSpacing: -0.2,
    ...uiFont(600),
  } as TextStyle,
  masterTagline: {
    fontSize: Platform.OS === 'web' ? 15 : 10,
    fontWeight: '500',
    letterSpacing: Platform.OS === 'web' ? 1.6 : 0.8,
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.55)',
    marginBottom: 4,
  } as TextStyle,
  mobileCluster: {
    gap: 16,
    paddingTop: 32,
  } as ViewStyle,
  mobileRibbon: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 8,
    paddingHorizontal: 4,
    paddingBottom: 8,
  } as ViewStyle,
  // ── Hero CTA ──
  taglineText: {
    fontSize: 24,
    ...uiFont(400),
    lineHeight: 38,
    letterSpacing: -0.1,
    marginBottom: 44,
    maxWidth: 600,
    opacity: 0.7,
  } as TextStyle,
  ctaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 36,
  } as ViewStyle,
  ctaPrimary: {
    backgroundColor: '#E98245',
    paddingHorizontal: 28,
    paddingVertical: 14,
    borderRadius: 100,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      boxShadow: '0 6px 24px rgba(237, 116, 61, 0.36)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    }),
  } as any,
  ctaPrimaryText: {
    fontSize: 14,
    ...uiFont(700),
    color: ON_ACCENT_TEXT,
    letterSpacing: 0.4,
  } as TextStyle,
  ctaSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 4,
    paddingVertical: 14,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  } as any,
  ctaSecondaryText: {
    fontSize: 14,
    ...uiFont(500),
    letterSpacing: 0.1,
    opacity: 0.7,
  } as TextStyle,
});
